/**
 * RewindMarker — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.rewindMarker,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { PixiCamera } from '../PixiCamera.js';
import { pixiHudLogger } from './logger.js';

/**
 * Create rewind start position marker
 */
export function create(hud) {
  const container = new PIXI.Container();
  container.visible = false;
  hud.hudContainer.addChild(container);

  // On-screen marker (flag/pin)
  const onScreenMarker = new PIXI.Container();
  
  // Flag pole
  const pole = new PIXI.Graphics();
  pole.setStrokeStyle({ width: 3, color: 0x00d4ff });
  pole.moveTo(0, 0);
  pole.lineTo(0, -40);
  pole.stroke();
  onScreenMarker.addChild(pole);

  // Flag triangle
  const flag = new PIXI.Graphics();
  flag.setFillStyle({ color: 0x00d4ff, alpha: 0.6 });
  flag.setStrokeStyle({ width: 3, color: 0x00d4ff });
  flag.moveTo(0, -40);
  flag.lineTo(25, -30);
  flag.lineTo(0, -20);
  flag.closePath();
  flag.fill();
  flag.stroke();
  onScreenMarker.addChild(flag);

  // Pin base
  const base = new PIXI.Graphics();
  base.setFillStyle({ color: 0x00d4ff, alpha: 0.6 });
  base.setStrokeStyle({ width: 3, color: 0x00d4ff });
  base.circle(0, 0, 6);
  base.fill();
  base.stroke();
  onScreenMarker.addChild(base);

  // Label text
  const label = new PIXI.Text({
    text: 'REWIND START',
    style: {
      fontFamily: 'Arial',
      fontSize: 14,
      fontWeight: 'bold',
      fill: 0x00d4ff,
      stroke: { color: 0x000000, width: 3 }
    }
  });
  label.anchor.set(0.5, 1);
  label.y = -55;
  onScreenMarker.addChild(label);

  container.addChild(onScreenMarker);

  // Off-screen marker (arrow)
  const offScreenMarker = new PIXI.Container();
  
  // Arrow
  const arrow = new PIXI.Graphics();
  arrow.setFillStyle({ color: 0x00d4ff, alpha: 0.6 });
  arrow.setStrokeStyle({ width: 3, color: 0x00d4ff });
  arrow.moveTo(20, 0);
  arrow.lineTo(-10, -15);
  arrow.lineTo(-10, 15);
  arrow.closePath();
  arrow.fill();
  arrow.stroke();
  offScreenMarker.addChild(arrow);

  // Distance text
  const distanceText = new PIXI.Text({
    text: '0m',
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'bold',
      fill: 0x00d4ff,
      stroke: { color: 0x000000, width: 3 }
    }
  });
  distanceText.anchor.set(0.5, 0.5);
  offScreenMarker.addChild(distanceText);

  container.addChild(offScreenMarker);

  hud.elements.rewindMarker = {
    container,
    onScreenMarker,
    offScreenMarker,
    label,
    distanceText,
    pole,
    flag,
    base,
    arrow
  };
}

/**
 * Update rewind marker position and visibility
 * @param {Object} gameState - Game state
 */
export function update(hud, gameState) {
  const rm = hud.elements.rewindMarker;
  if (!rm) {
    pixiHudLogger.warn('[RewindMarker] No rewindMarker element!');
    return;
  }

  // Get rewind effect from gameState.abilityState (where AbilityEvents stores it)
  const abilityState = gameState.abilityState;
  const rewindEffect = abilityState?.activeEffects?.['rewind'];


  if (!rewindEffect || !rewindEffect.rewindStartX || !rewindEffect.rewindStartY) {
    rm.container.visible = false;
    return;
  }

  const worldX = rewindEffect.rewindStartX;
  const worldY = rewindEffect.rewindStartY;

  // Use PixiCamera for world-to-screen conversion (camera is global, not on gameState)
  if (typeof PixiCamera === 'undefined' || !PixiCamera.screenWidth) {
    pixiHudLogger.warn('[RewindMarker] PixiCamera not available!');
    rm.container.visible = false;
    return;
  }

  // Convert world to screen coordinates using PixiCamera
  const screenPos = PixiCamera.worldToScreen(worldX, worldY);
  const screenX = screenPos.x;
  const screenY = screenPos.y;

  // Pulse animation
  const time = Date.now();
  const pulse = Math.sin(time * 0.005) * 0.3 + 0.7;

  // Check if on-screen
  const margin = 50;
  const isOnScreen = screenX >= margin && screenX <= hud.screenWidth - margin &&
                     screenY >= margin && screenY <= hud.screenHeight - margin;

  rm.container.visible = true;

  if (isOnScreen) {
    // Show on-screen marker
    rm.onScreenMarker.visible = true;
    rm.offScreenMarker.visible = false;
    rm.container.x = screenX;
    rm.container.y = screenY;
    rm.onScreenMarker.alpha = pulse;
  } else {
    // Show off-screen indicator
    rm.onScreenMarker.visible = false;
    rm.offScreenMarker.visible = true;

    const centerX = hud.screenWidth / 2;
    const centerY = hud.screenHeight / 2;
    const dx = screenX - centerX;
    const dy = screenY - centerY;
    const angle = Math.atan2(dy, dx);

    // Position on edge
    const edgeMargin = 60;
    const edgeX = centerX + Math.cos(angle) * (hud.screenWidth / 2 - edgeMargin);
    const edgeY = centerY + Math.sin(angle) * (hud.screenHeight / 2 - edgeMargin);

    // Clamp to bounds
    const clampedX = Math.max(edgeMargin, Math.min(hud.screenWidth - edgeMargin, edgeX));
    const clampedY = Math.max(edgeMargin, Math.min(hud.screenHeight - edgeMargin, edgeY));

    rm.container.x = clampedX;
    rm.container.y = clampedY;
    rm.offScreenMarker.rotation = angle;
    rm.offScreenMarker.alpha = pulse;

    // Calculate distance (in world units, no zoom scaling needed)
    const distance = Math.sqrt(dx * dx + dy * dy);
    rm.distanceText.text = `${Math.round(distance)}m`;
  }
}
